import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background,
  BackgroundVariant,
  BaseEdge,
  Controls,
  Handle,
  MiniMap,
  Position,
  ReactFlowProvider,
  getBezierPath,
  useNodesState,
  useReactFlow,
  useViewport,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeDragHandler,
  type NodeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import type { OrgChartPerson, OrgDepartment, OrgLevel } from '../../../types/purchasing';
import {
  hitTestOrgDrop,
  ORG_BOT_CASTE_EDGE_ID,
  ORG_EDGE_TYPE,
  ORG_LAYOUT,
  type OrgChartCellRect,
  type OrgChartPoolRect,
  type OrgPersonNodeData,
  type OrgPoolNodeData,
} from '../../../utils/orgChartLayout';
import { isOrgChartBot } from '../../../utils/orgChart';
import { canManage } from '../../../utils/orgReporting';
import OrgPersonNode from './OrgPersonNode';
import '../../board/InspirationBoard.css';

type Graph = {
  nodes: Node[];
  edges: Edge[];
  cells: OrgChartCellRect[];
  pool: OrgChartPoolRect | null;
  width: number;
  height: number;
};

type BoardProps = {
  graph: Graph;
  canEdit: boolean;
  selectedUid: string | null;
  onSelect: (uid: string | null) => void;
  onDropCell: (level: OrgLevel, department: OrgDepartment, uid: string) => void;
  onDropPerson: (target: OrgChartPerson, uid: string) => void;
};

type DragApi = {
  canEdit: boolean;
  draggingUid: string | null;
  dropCell: string | null;
  dropPersonUid: string | null;
  onSelect: (uid: string | null) => void;
};

const DragContext = React.createContext<DragApi>({
  canEdit: false,
  draggingUid: null,
  dropCell: null,
  dropPersonUid: null,
  onSelect: () => undefined,
});

function OrgPersonFlowNode({ data, selected }: NodeProps<OrgPersonNodeData>) {
  const { draggingUid, dropPersonUid, onSelect } = React.useContext(DragContext);
  const person = data.person;

  return (
    <>
      <Handle type="target" position={Position.Top} className="people-org-handle" />
      <OrgPersonNode
        person={person}
        selected={selected}
        dragging={draggingUid === person.uid}
        dropTarget={dropPersonUid === person.uid}
        compact={data.compact}
        onSelect={() => onSelect(person.uid)}
      />
      <Handle type="source" position={Position.Bottom} className="people-org-handle" />
    </>
  );
}

/** Invisible anchor so the CEO -> Bot Caste edge has one target for the whole group. */
function OrgBotCasteFlowNode({ data }: NodeProps<OrgPoolNodeData>) {
  return (
    <div className="people-org-caste-anchor" aria-label={`${data.label} (${data.count})`}>
      <Handle type="target" position={Position.Top} className="people-org-handle" />
    </div>
  );
}

/** Invisible anchor so the COO -> pool edge has a target; the frame is drawn in the grid overlay. */
function OrgPoolFlowNode({ data }: NodeProps<OrgPoolNodeData>) {
  return (
    <div
      className="people-org-pool-anchor"
      style={{ width: ORG_LAYOUT.poolWidth, height: ORG_LAYOUT.poolHeaderHeight }}
      aria-label={`${data.label} (${data.count})`}
    >
      <Handle type="target" position={Position.Top} className="people-org-handle" />
    </div>
  );
}

/**
 * Leave the CEO, step into the aisle between Office and Shop, then drop
 * straight onto the top of the Bot Caste box.
 */
function botCastePath(sourceX: number, sourceY: number, targetX: number, targetY: number): string {
  const radius = 22;
  const dir = targetX >= sourceX ? 1 : -1;
  const shoulder = sourceY + 28;
  const railY = shoulder + radius;
  const approach = targetX - dir * radius;
  return [
    `M${sourceX},${sourceY}`,
    `L${sourceX},${shoulder}`,
    `Q${sourceX},${railY} ${sourceX + dir * radius},${railY}`,
    `L${approach},${railY}`,
    `Q${targetX},${railY} ${targetX},${railY + radius}`,
    `L${targetX},${targetY}`,
  ].join(' ');
}

function OrgBezierEdge({
  id,
  sourceX,
  sourceY,
  sourcePosition,
  targetX,
  targetY,
  targetPosition,
  style,
}: EdgeProps) {
  const path = id === ORG_BOT_CASTE_EDGE_ID
    ? botCastePath(sourceX, sourceY, targetX, targetY)
    : getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      curvature: 0.4,
    })[0];
  return <BaseEdge id={id} path={path} style={style} />;
}

const nodeTypes = {
  orgPerson: OrgPersonFlowNode,
  orgPool: OrgPoolFlowNode,
  orgBotCaste: OrgBotCasteFlowNode,
};
const edgeTypes = { [ORG_EDGE_TYPE]: OrgBezierEdge };

function personOf(node: Node): OrgChartPerson | null {
  return node.data && 'person' in node.data ? (node.data as OrgPersonNodeData).person : null;
}

function OrgChartGrid({ graph, layer }: { graph: Graph; layer: 'back' | 'front' }) {
  const { x, y, zoom } = useViewport();
  const { canEdit, draggingUid, dropCell, dropPersonUid } = React.useContext(DragContext);
  const lanes = graph.nodes.filter(n => n.type === 'orgLane');
  const cols = graph.nodes.filter(n => n.type === 'orgColLabel');
  const people = graph.nodes.filter(n => n.type === 'orgPerson');
  const roster = people.map(personOf).filter((p): p is OrgChartPerson => !!p);
  const dragging = draggingUid ? roster.find(p => p.uid === draggingUid) || null : null;
  const laneWidth = graph.pool ? graph.pool.x - ORG_LAYOUT.poolGapX : graph.width;
  const frameStyle = {
    width: graph.width,
    height: graph.height,
    transform: `translate(${x}px, ${y}px) scale(${zoom})`,
  };

  if (layer === 'back') {
    return (
      <div className="people-org-grid is-back" style={frameStyle}>
        {lanes.map(node => (
          <div
            key={node.id}
            className={`people-org-lane is-lane-${String(node.data && 'level' in node.data ? node.data.level : '')}`}
            style={{
              left: node.position.x,
              top: node.position.y,
              width: laneWidth,
              height: node.style?.height,
            }}
          />
        ))}
        {graph.pool && (
          <div
            className="people-org-pool"
            style={{
              left: graph.pool.x,
              top: graph.pool.y,
              width: graph.pool.width,
              height: graph.pool.height,
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="people-org-grid is-front" style={frameStyle}>
      {lanes.map(node => {
        const level = node.data && 'level' in node.data ? String(node.data.level) : '';
        const label = String(node.data && 'label' in node.data ? node.data.label : '');
        return (
          <React.Fragment key={node.id}>
            <span
              className={`people-org-lane-label is-lane-${level}`}
              style={{ left: node.position.x + 12, top: node.position.y + 12 }}
            >
              {label}
            </span>
            {level === 'bots' && (
              <span
                className="people-org-lane-caption"
                style={{ left: node.position.x + 118, top: node.position.y + 16 }}
              >
                {roster.filter(isOrgChartBot).length} · reports to the CEO
              </span>
            )}
          </React.Fragment>
        );
      })}
      {cols.map(node => (
        <div
          key={node.id}
          className={`people-org-col-label is-group-${String(node.data && 'department' in node.data ? node.data.department : '')}`}
          style={{
            left: node.position.x,
            top: node.position.y,
            width: ORG_LAYOUT.cellWidth,
            height: ORG_LAYOUT.colHeaderHeight,
          }}
        >
          {String(node.data && 'label' in node.data ? node.data.label : '')}
        </div>
      ))}
      {graph.pool && (
        <div
          className="people-org-pool-header"
          style={{
            left: graph.pool.x,
            top: graph.pool.y,
            width: graph.pool.width,
            height: graph.pool.headerHeight,
          }}
        >
          <span className="people-org-pool-title">{graph.pool.label}</span>
          <span className="people-org-pool-sub">
            {graph.pool.count} · reports to the COO
          </span>
        </div>
      )}
      {canEdit && draggingUid && graph.cells.filter(cell => cell.droppable).map(cell => {
        const key = `${cell.level}:${cell.department}`;
        return (
          <div
            key={key}
            className={`people-org-drop${dropCell === key ? ' is-active' : ''}`}
            style={{ left: cell.x, top: cell.y, width: cell.width, height: cell.height }}
          />
        );
      })}
      {canEdit && dragging && people.map(node => {
        const person = personOf(node);
        if (!person || person.uid === dragging.uid) return null;
        if (!canManage(person, dragging, roster)) return null;
        return (
          <div
            key={`drop-${person.uid}`}
            className={`people-org-person-drop${dropPersonUid === person.uid ? ' is-active' : ''}`}
            style={{
              left: node.position.x,
              top: node.position.y,
              width: ORG_LAYOUT.cardWidth,
              height: ORG_LAYOUT.cardHeight,
            }}
          />
        );
      })}
    </div>
  );
}

function OrgChartBoardInner({
  graph,
  canEdit,
  selectedUid,
  onSelect,
  onDropCell,
  onDropPerson,
}: BoardProps) {
  const rf = useReactFlow();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const didFit = useRef(false);
  const paneReady = useRef(false);
  const [draggingUid, setDraggingUid] = useState<string | null>(null);
  const [dropCell, setDropCell] = useState<string | null>(null);
  const [dropPersonUid, setDropPersonUid] = useState<string | null>(null);

  const layoutNodes = useMemo(
    () => graph.nodes
      .filter(n => n.type === 'orgPerson' || n.type === 'orgPool' || n.type === 'orgBotCaste')
      .map(n => {
        const person = personOf(n);
        const pinned = !person || person.pinned === true;
        return {
          ...n,
          draggable: canEdit && n.type === 'orgPerson' && !pinned,
          selected: n.id === selectedUid,
        };
      }),
    [graph.nodes, selectedUid, canEdit],
  );

  const [nodes, setNodes, onNodesChange] = useNodesState(layoutNodes);

  useEffect(() => {
    if (draggingUid) return;
    setNodes(layoutNodes);
  }, [draggingUid, layoutNodes, setNodes]);

  const peopleForHit = useMemo(
    () => layoutNodes
      .map(n => {
        const person = personOf(n);
        if (!person) return null;
        return { uid: person.uid, x: n.position.x, y: n.position.y, person };
      })
      .filter((p): p is { uid: string; x: number; y: number; person: OrgChartPerson } => !!p),
    [layoutNodes],
  );

  const endDrag = useCallback(() => {
    setDraggingUid(null);
    setDropCell(null);
    setDropPersonUid(null);
  }, []);

  const applyPointerHit = useCallback((event: React.MouseEvent, uid: string) => {
    const point = rf.screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const hit = hitTestOrgDrop(point, uid, peopleForHit, graph.cells);
    if (hit?.type === 'person') {
      setDropPersonUid(hit.person.uid);
      setDropCell(null);
      return hit;
    }
    if (hit?.type === 'cell') {
      setDropPersonUid(null);
      setDropCell(`${hit.level}:${hit.department}`);
      return hit;
    }
    setDropPersonUid(null);
    setDropCell(null);
    return null;
  }, [graph.cells, peopleForHit, rf]);

  const handleNodeDragStart: NodeDragHandler = useCallback((_, node) => {
    setDraggingUid(node.id);
    onSelect(node.id);
  }, [onSelect]);

  const handleNodeDrag: NodeDragHandler = useCallback((event, node) => {
    applyPointerHit(event, node.id);
  }, [applyPointerHit]);

  const handleNodeDragStop: NodeDragHandler = useCallback((event, node) => {
    const hit = applyPointerHit(event, node.id);
    if (hit?.type === 'person') onDropPerson(hit.person, node.id);
    else if (hit?.type === 'cell') onDropCell(hit.level, hit.department, node.id);
    else setNodes(layoutNodes);
    endDrag();
  }, [applyPointerHit, endDrag, layoutNodes, onDropCell, onDropPerson, setNodes]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey) event.preventDefault();
    };
    const onGesture = (event: Event) => event.preventDefault();
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('gesturestart', onGesture);
    el.addEventListener('gesturechange', onGesture);
    el.addEventListener('gestureend', onGesture);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('gesturestart', onGesture);
      el.removeEventListener('gesturechange', onGesture);
      el.removeEventListener('gestureend', onGesture);
    };
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el || layoutNodes.length === 0) return;
    const fit = () => {
      const { width, height } = el.getBoundingClientRect();
      const paneH = el.querySelector('.react-flow__pane')?.getBoundingClientRect().height || 0;
      if (width < 8 || height < 8 || paneH < 8) return;
      if (didFit.current && paneReady.current) return;
      didFit.current = true;
      paneReady.current = true;
      rf.fitView({ padding: 0.28, duration: 280 });
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    const flow = el.querySelector('.react-flow');
    if (flow) observer.observe(flow);
    return () => observer.disconnect();
  }, [layoutNodes.length, rf]);

  const dragApi = useMemo<DragApi>(() => ({
    canEdit,
    draggingUid,
    dropCell,
    dropPersonUid,
    onSelect,
  }), [canEdit, draggingUid, dropCell, dropPersonUid, onSelect]);

  return (
    <DragContext.Provider value={dragApi}>
      <div
        ref={wrapperRef}
        className={`inspiration-board people-org-board${draggingUid ? ' is-dragging' : ''}`}
      >
        <OrgChartGrid graph={graph} layer="back" />
        <ReactFlow
          nodes={nodes}
          edges={graph.edges}
          onNodesChange={onNodesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={{ type: ORG_EDGE_TYPE }}
          nodesDraggable={canEdit}
          nodesConnectable={false}
          elementsSelectable
          selectNodesOnDrag={false}
          selectionOnDrag={false}
          panOnDrag
          panOnScroll
          preventScrolling
          zoomOnDoubleClick={false}
          minZoom={0.1}
          maxZoom={4}
          proOptions={{ hideAttribution: true }}
          className="people-org-flow"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          onPaneClick={() => onSelect(null)}
          onNodeClick={(_, node) => {
            if (node.type === 'orgPerson') onSelect(node.id);
          }}
          onNodeDragStart={canEdit ? handleNodeDragStart : undefined}
          onNodeDrag={canEdit ? handleNodeDrag : undefined}
          onNodeDragStop={canEdit ? handleNodeDragStop : undefined}
        >
          <Background variant={BackgroundVariant.Dots} gap={22} size={1.4} color="#d8dee9" />
          <Controls showInteractive={false} />
          <MiniMap maskColor="rgba(244,246,250,0.7)" pannable zoomable />
        </ReactFlow>
        <OrgChartGrid graph={graph} layer="front" />
      </div>
    </DragContext.Provider>
  );
}

const OrgChartBoard: React.FC<BoardProps> = props => (
  <ReactFlowProvider>
    <OrgChartBoardInner {...props} />
  </ReactFlowProvider>
);

export default OrgChartBoard;
